import { useEffect, useRef, useCallback } from 'react';
import { useMeetingSelector } from '../meeting/meetingProviderContextDef';
import { rejectNotJoined } from '../utils';
const NOOP = () => {};

const selectMeeting = s => s.meeting;
const selectIsMeetingJoined = s => s.isMeetingJoined;

/**
 *
 * @param {string} key
 * @param {{
 *  onValueChanged?: Function,
 * }} options
 * @returns {{
 *  setValue: (value : string) => void
 *  getValue: () => string
 * }}
 */
const useRealtimeStore = (key, { onValueChanged = NOOP } = {}) => {
  const meeting = useMeetingSelector(selectMeeting);
  const isMeetingJoined = useMeetingSelector(selectIsMeetingJoined);

  const observerIdRef = useRef(null);
  const isObserving = useRef(false);
  const meetingRef = useRef(meeting);
  const onValueChangedRef = useRef(onValueChanged);

  meetingRef.current = meeting;
  onValueChangedRef.current = onValueChanged;
  /**
   * Set or update a key-value pair in the RealtimeStore.
   *
   * @async
   * @param {String|null} value - The value to set (string only). Pass `null` to delete the key.
   * @returns {Promise<void>}
   *
   * @throws {Error} If value is not a string or exceeds 1 KB.
   * @throws {Error} If request fails.
   */
  const setValue = useCallback(
    value => {
      if (!meetingRef.current) return rejectNotJoined('setValue');
      return meetingRef.current.realtimeStore.setValue(key, value);
    },
    [key]
  );

  /**
   * Retrieve the value associated with a key from the RealtimeStore.
   *
   * @async
   * @returns {Promise<String|null>} - Returns the stored value from the RealtimeStore..
   *
   * @throws {Error} If key doesn't exist or request fails.
   */
  const getValue = useCallback(() => {
    if (!meetingRef.current) return rejectNotJoined('getValue');
    return meetingRef.current.realtimeStore.getValue(key);
  }, [key]);

  const _handleRealtimeStore = useCallback(data => {
    onValueChangedRef.current(data);
  }, []);

  useEffect(() => {
    if (!meeting || isObserving.current || !isMeetingJoined || !onValueChanged)
      return;

    isObserving.current = true;

    let cancelled = false;

    const subscribeToKey = async () => {
      try {
        const observer = await meeting?.realtimeStore?.observe(
          key,
          _handleRealtimeStore
        );
        if (cancelled) {
          // stopObserving never rejects, so no .catch() needed.
          meetingRef.current?.realtimeStore?.stopObserving(observer);
        } else if (observer) {
          observerIdRef.current = observer;
        }
      } catch (error) {
        // Let a later effect run retry instead of leaving the hook wedged as
        // "observing" against a subscription the SDK never registered.
        if (!cancelled) isObserving.current = false;
      }
    };

    subscribeToKey();

    return () => {
      cancelled = true;
      if (observerIdRef.current) {
        meetingRef.current?.realtimeStore?.stopObserving(observerIdRef.current);
        observerIdRef.current = null;
      }
      isObserving.current = false;
    };
  }, [meeting, isMeetingJoined, key, _handleRealtimeStore]);

  return { setValue, getValue };
};

export default useRealtimeStore;
